/**
 * Canvas Editor type definitions
 *
 * View state and behavior types for the canvas editing layer.
 * View state is ephemeral, local-only state for in-progress operations.
 */

import type { WorldPoint } from "@rool/renderer/types";
import { localToWorld } from "./geometry";

// =============================================================================
// Point (simplified for editor - uses WorldPoint from canvas)
// =============================================================================

export type Point = WorldPoint;

// =============================================================================
// View State
// =============================================================================

/**
 * Ephemeral view state for canvas editing.
 * Never synced or persisted - local to this client.
 */
export interface ViewState {
  /** Currently selected node IDs */
  selection: Set<string>;

  /** Anchor node for shift-click range selection */
  selectionAnchor: string | null;

  /** Box selection in progress */
  boxSelect: {
    origin: Point;
    current: Point;
  } | null;

  /** Drag operation in progress */
  drag: {
    nodes: string[];
    origin: Point;
    current: Point;
    copying: boolean;
  } | null;

  /** Edge connection in progress */
  connecting: {
    sourceNode: string;
    sourceHandle: string;
    currentPoint: Point;
  } | null;

  /** Inline editing state */
  editing: {
    nodeId: string;
    field: string;
  } | null;

  /** Hover state for visual feedback */
  hovered: {
    nodeId: string | null;
    edgeId: string | null;
    handleId: string | null;
    resizeHandleId: string | null;
    /** True when hovering in rotation zone outside corners */
    rotationZone: boolean;
  };

  /** Resize operation in progress */
  resize: {
    nodeId: string;
    handle: ResizeHandlePosition;
    /** Node rotation in radians (fixed during resize) */
    rotation: number;
    /** Anchor point in local coordinates (opposite corner/edge) */
    anchorLocal: Point;
    /** Original bounds with center coordinates */
    originalBounds: { cx: number; cy: number; width: number; height: number };
    /** Current bounds with center coordinates */
    currentBounds: { cx: number; cy: number; width: number; height: number };
  } | null;

  /** Rotation operation in progress */
  rotate: {
    nodeId: string;
    /** Center point of the node (rotation pivot) */
    centerPoint: Point;
    /** Angle in radians at drag start */
    startAngle: number;
    /** Current angle in radians */
    currentAngle: number;
    /** Node's original rotation before this operation */
    originalRotation: number;
  } | null;

  /** Whether files are being dragged over the canvas (drop target indicator) */
  dropTarget: boolean;
}

// =============================================================================
// Selection Modes
// =============================================================================

/** How to apply a selection operation */
export type SelectionMode = "replace" | "add" | "remove" | "toggle";

// =============================================================================
// Box Selection
// =============================================================================

/** Rectangle for box selection */
export interface SelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// =============================================================================
// Node Data - Canonical node type for the editor
// =============================================================================

/**
 * Canonical node type for the canvas editor.
 * Position is top-left in world coordinates (matches SDK and CSS convention).
 * Contains all fields needed by editor, renderer, and app.
 */
export interface CanvasNode {
  id: string;
  /** Node type (e.g. 'markdown', 'topic', 'image') */
  type: string;
  /** Position in world coordinates (top-left corner) */
  position: Point;
  /** Size in world units */
  size: { width: number; height: number };
  /** Z-order for rendering/hit testing - higher values are "on top" */
  z: number;
  /** Rotation in radians (optional, defaults to 0) */
  rotation?: number;
  /** Semantic data for rendering (type-specific) */
  data: Record<string, unknown>;
  /** Visual state flags */
  selected?: boolean;
  hovered?: boolean;
  dragging?: boolean;
  resizing?: boolean;
  rotating?: boolean;
  /** Optional CSS class override */
  className?: string;
}

/** @deprecated Use CanvasNode instead */
export type EditorNode = CanvasNode;

// =============================================================================
// Edge Data - Canonical edge type for the editor
// =============================================================================

/**
 * Canonical edge type for the canvas editor.
 * Uses `source`/`target` matching renderer convention.
 */
export interface CanvasEdge {
  id: string;
  /** Edge type */
  type: string;
  /** Source node ID */
  source: string;
  /** Target node ID */
  target: string;
  /** Semantic data for rendering */
  data: Record<string, unknown>;
  /** Visual state flags */
  selected?: boolean;
  hovered?: boolean;
  /** @deprecated Use `source` instead */
  sourceNodeId: string;
  /** @deprecated Use `target` instead */
  targetNodeId: string;
}

/** @deprecated Use CanvasEdge instead */
export type EditorEdge = CanvasEdge;

// =============================================================================
// View State Change Callback
// =============================================================================

export type ViewStateChangeCallback = (state: ViewState) => void;

// =============================================================================
// Connection Handles
// =============================================================================

/** Handle positions on a node */
export type HandlePosition = "top" | "right" | "bottom" | "left";

/** Full handle identifier: nodeId + position */
export interface HandleId {
  nodeId: string;
  position: HandlePosition;
}

/** Parse a handle ID string (format: "nodeId:position") */
export function parseHandleId(handleIdStr: string): HandleId | null {
  const parts = handleIdStr.split(":");
  if (parts.length !== 2) return null;
  const position = parts[1] as HandlePosition;
  if (!["top", "right", "bottom", "left"].includes(position)) return null;
  return { nodeId: parts[0], position };
}

/** Format a handle ID as a string */
export function formatHandleId(
  nodeId: string,
  position: HandlePosition,
): string {
  return `${nodeId}:${position}`;
}

/** Get the world position of a handle on a node */
export function getHandlePosition(
  node: EditorNode,
  position: HandlePosition,
): Point {
  const { x, y } = node.position;
  const { width, height } = node.size;

  switch (position) {
    case "top":
      return { x: x + width / 2, y };
    case "right":
      return { x: x + width, y: y + height / 2 };
    case "bottom":
      return { x: x + width / 2, y: y + height };
    case "left":
      return { x, y: y + height / 2 };
  }
}

/** Get all handle positions for a node */
export function getAllHandlePositions(
  node: EditorNode,
): Array<{ position: HandlePosition; point: Point }> {
  const positions: HandlePosition[] = ["top", "right", "bottom", "left"];
  return positions.map((position) => ({
    position,
    point: getHandlePosition(node, position),
  }));
}

// =============================================================================
// Resize Handles
// =============================================================================

/** Resize handle positions: 4 corners + 4 edge midpoints */
export type ResizeHandlePosition =
  | "nw"
  | "n"
  | "ne"
  | "w"
  | "e"
  | "sw"
  | "s"
  | "se";

/** All resize handle positions in order */
export const RESIZE_HANDLE_POSITIONS: ResizeHandlePosition[] = [
  "nw",
  "n",
  "ne",
  "w",
  "e",
  "sw",
  "s",
  "se",
];

/** Parse a resize handle ID string (format: "resize:nodeId:position") */
export function parseResizeHandleId(
  handleIdStr: string,
): { nodeId: string; position: ResizeHandlePosition } | null {
  const parts = handleIdStr.split(":");
  if (parts.length !== 3 || parts[0] !== "resize") return null;
  const position = parts[2] as ResizeHandlePosition;
  if (!RESIZE_HANDLE_POSITIONS.includes(position)) return null;
  return { nodeId: parts[1], position };
}

/** Format a resize handle ID as a string */
export function formatResizeHandleId(
  nodeId: string,
  position: ResizeHandlePosition,
): string {
  return `resize:${nodeId}:${position}`;
}

/** Get the world position of a resize handle on a node */
export function getResizeHandlePosition(
  node: EditorNode,
  position: ResizeHandlePosition,
): Point {
  const { x, y } = node.position;
  const { width, height } = node.size;

  switch (position) {
    case "nw":
      return { x, y };
    case "n":
      return { x: x + width / 2, y };
    case "ne":
      return { x: x + width, y };
    case "w":
      return { x, y: y + height / 2 };
    case "e":
      return { x: x + width, y: y + height / 2 };
    case "sw":
      return { x, y: y + height };
    case "s":
      return { x: x + width / 2, y: y + height };
    case "se":
      return { x: x + width, y: y + height };
  }
}

/** Get all resize handle positions for a node (axis-aligned, ignores rotation) */
export function getAllResizeHandlePositions(
  node: EditorNode,
): Array<{ position: ResizeHandlePosition; point: Point }> {
  return RESIZE_HANDLE_POSITIONS.map((position) => ({
    position,
    point: getResizeHandlePosition(node, position),
  }));
}

/**
 * Get the world position of a resize handle on a rotated node.
 * The handle position is calculated in local space then rotated to world space.
 */
export function getRotatedResizeHandlePosition(
  node: EditorNode,
  position: ResizeHandlePosition,
): Point {
  const rotation = node.rotation ?? 0;

  // If no rotation, use the simple version
  if (rotation === 0) {
    return getResizeHandlePosition(node, position);
  }

  // Calculate node center
  const { x, y } = node.position;
  const { width, height } = node.size;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const hw = width / 2;
  const hh = height / 2;

  // Get handle position in local coordinates (relative to center)
  let localX: number;
  let localY: number;

  switch (position) {
    case "nw":
      localX = -hw;
      localY = -hh;
      break;
    case "n":
      localX = 0;
      localY = -hh;
      break;
    case "ne":
      localX = hw;
      localY = -hh;
      break;
    case "w":
      localX = -hw;
      localY = 0;
      break;
    case "e":
      localX = hw;
      localY = 0;
      break;
    case "sw":
      localX = -hw;
      localY = hh;
      break;
    case "s":
      localX = 0;
      localY = hh;
      break;
    case "se":
      localX = hw;
      localY = hh;
      break;
  }

  // Transform to world coordinates
  return localToWorld({ x: localX, y: localY }, { x: cx, y: cy }, rotation);
}

/**
 * Get all resize handle positions for a rotated node.
 * Returns positions in world coordinates with rotation applied.
 */
export function getAllRotatedResizeHandlePositions(
  node: EditorNode,
): Array<{ position: ResizeHandlePosition; point: Point }> {
  return RESIZE_HANDLE_POSITIONS.map((position) => ({
    position,
    point: getRotatedResizeHandlePosition(node, position),
  }));
}

/** Get the anchor point for a resize operation (opposite corner/edge) */
export function getResizeAnchorPosition(
  node: EditorNode,
  handle: ResizeHandlePosition,
): Point {
  const { x, y } = node.position;
  const { width, height } = node.size;

  // Map each handle to its opposite anchor
  switch (handle) {
    case "nw":
      return { x: x + width, y: y + height }; // Anchor at SE
    case "n":
      return { x: x + width / 2, y: y + height }; // Anchor at S (center)
    case "ne":
      return { x, y: y + height }; // Anchor at SW
    case "w":
      return { x: x + width, y: y + height / 2 }; // Anchor at E (center)
    case "e":
      return { x, y: y + height / 2 }; // Anchor at W (center)
    case "sw":
      return { x: x + width, y }; // Anchor at NE
    case "s":
      return { x: x + width / 2, y }; // Anchor at N (center)
    case "se":
      return { x, y }; // Anchor at NW
  }
}

/** Cursor style for each resize handle (at 0 rotation) */
export const RESIZE_CURSORS: Record<ResizeHandlePosition, string> = {
  nw: "nwse-resize",
  n: "ns-resize",
  ne: "nesw-resize",
  w: "ew-resize",
  e: "ew-resize",
  sw: "nesw-resize",
  s: "ns-resize",
  se: "nwse-resize",
};

/** Base angles for resize handles (degrees, 0 = east, clockwise) */
const HANDLE_ANGLES: Record<ResizeHandlePosition, number> = {
  e: 0,
  se: 45,
  s: 90,
  sw: 135,
  w: 180,
  nw: 225,
  n: 270,
  ne: 315,
};

/** Cursor types in order (every 45 degrees starting from east) */
const CURSOR_SEQUENCE = [
  "ew-resize",
  "nwse-resize",
  "ns-resize",
  "nesw-resize",
];

/**
 * Get the appropriate resize cursor for a handle on a rotated node.
 * The cursor rotates with the node so e.g. "east" handle on a 45° rotated node
 * shows a NE/SW cursor instead of E/W cursor.
 *
 * @param handle - The resize handle position
 * @param rotation - Node rotation in radians
 * @returns CSS cursor value
 */
export function getRotatedResizeCursor(
  handle: ResizeHandlePosition,
  rotation: number,
): string {
  // Convert rotation to degrees and add to handle's base angle
  const baseAngle = HANDLE_ANGLES[handle];
  const rotationDeg = (rotation * 180) / Math.PI;
  const totalAngle = baseAngle + rotationDeg;

  // Normalize to 0-180 (cursors repeat every 180 degrees)
  const normalized = ((totalAngle % 180) + 180) % 180;

  // Map to cursor index (every 45 degrees)
  const index = Math.round(normalized / 45) % 4;
  return CURSOR_SEQUENCE[index];
}

// =============================================================================
// Hit Test Result
// =============================================================================

export interface HitTestResult {
  nodeId: string | null;
  edgeId: string | null;
  /** Handle ID in format "nodeId:position" */
  handleId: string | null;
  /** Resize handle ID in format "resize:nodeId:position" */
  resizeHandleId: string | null;
}

// =============================================================================
// Change Events
// =============================================================================

/**
 * Unified change event emitted by CanvasEditor for all data mutations.
 * All values are final (already snapped, converted, etc.).
 * Use this for persistence / syncing changes to a backend.
 */
export type CanvasChangeEvent =
  | { type: "move"; nodes: Array<{ id: string; position: Point }> }
  | {
      type: "resize";
      nodeId: string;
      position: Point;
      size: { width: number; height: number };
    }
  | { type: "rotate"; nodeId: string; rotation: number }
  | { type: "delete-nodes"; nodeIds: string[] }
  | { type: "delete-edges"; edgeIds: string[] }
  | { type: "create-edge"; sourceNodeId: string; targetNodeId: string }
  | { type: "duplicate"; newNodes: EditorNode[]; newEdges: EditorEdge[] }
  | {
      type: "edit";
      nodeId: string;
      content: string;
      size?: { width: number; height: number };
    }
  | { type: "layout"; movedNodes: Array<{ id: string; position: Point }> }
  | { type: "create-node"; node: EditorNode }
  | { type: "action"; nodeId: string; action: string };

// =============================================================================
// Alignment Guides
// =============================================================================

/** Type of alignment guide */
export type AlignmentGuideType = "edge" | "center" | "spacing";

/** A single alignment guide line */
export interface AlignmentGuide {
  /** Which axis the guide line runs along */
  axis: "x" | "y";
  /** World coordinate of the guide line */
  position: number;
  /** Which edge of the dragged node triggered this guide */
  sourceEdge: string;
  /** Guide type for styling */
  type: AlignmentGuideType;
  /** IDs of reference nodes that align with this guide */
  referenceNodeIds: string[];
}

/** A spacing guide showing equal gaps between nodes */
export interface SpacingGuide {
  /** Which axis the spacing is measured along */
  axis: "x" | "y";
  /** The gap distance */
  gap: number;
  /** Segments showing each equal gap */
  segments: Array<{ fromEdge: number; toEdge: number; center: number }>;
}

/** Result of guide snap computation */
export interface GuideSnapResult {
  /** Adjusted drag offset (snapped) */
  offset: Point;
  /** Active alignment guides to render */
  guides: AlignmentGuide[];
  /** Active spacing guides to render */
  spacingGuides: SpacingGuide[];
  /** Which axes were snapped by guides */
  snappedAxes: { x: boolean; y: boolean };
}

/** Result of resize guide snap computation */
export interface ResizeGuideSnapResult {
  /** Adjusted bounds (snapped, keeping anchor fixed) */
  bounds: { cx: number; cy: number; width: number; height: number };
  /** Active alignment guides to render */
  guides: AlignmentGuide[];
  /** Spacing guides (always [] for resize — keeps overlay compat) */
  spacingGuides: SpacingGuide[];
  /** Which axes were snapped by guides */
  snappedAxes: { x: boolean; y: boolean };
}

/** Bounding edges of a node */
export interface NodeEdges {
  top: number;
  bottom: number;
  left: number;
  right: number;
  centerX: number;
  centerY: number;
}
